import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';

// Store actions
import {
  uploadImageUrl,
  setError,
  setImageDimensions,
} from '../../../actions/upload';
import {
  setCropEndpointCalled,
  setCropData,
  setDrawingMode,
} from '../../../actions/crop';

// Examples components
import Examples from '../Examples/Examples';

// App Constants
import appRoutes from '../../../constants/appRoutes';
import { NO_ERROR } from '../../../constants/errorConstants';

// App Services
import ImageService from '../../../services/ImageService';

// Component styles
import './examplesContainer.scss';

/**
 * This container is linked to the store `upload` key and it wraps the Examples component.
 * - Update a image url when the image upload is successfull.
 * - Reset any error since the examples are valid images
 * @extends {Component}
 */
class ExamplesContainer extends React.Component {
  constructor(props) {
    super(props);

    this.onExampleClicked = this.onExampleClicked.bind(this);
  }

  /**
   * Callback passed to the Examples component
   * When the upload is successfull, this method will be triggered in order to dispatch the action that uploads the
   * imageUrl, reset the image crop data and the error if exists. Then it redirects to the `crop` page.
   * @param {String} imageUrl Url of the example image clicked.
   */
  onExampleClicked(imageUrl) {
    ImageService.getImageBlobFromImageUrl(imageUrl)
      .then((blobImage) => {
        // Update the url of the image to use
        this.props.uploadImageUrl(window.URL.createObjectURL(blobImage));

        // The cropping data should be reset
        this.props.setCropEndpointCalled(false);
        this.props.setCropData([]);

        // All examples are valid image urls, so no error should be displayed
        this.props.setError(NO_ERROR);

        ImageService.getImageMetadata(imageUrl).then((metadata) => {
          const { width, height } = metadata;
          this.props.setImageDimensions({ width, height });
          this.props.setDrawingMode(false);
          // Move to the second page to crop area detection
          this.props.redirectToCrop();
        });
      });
  }

  render() {
    return (
      <div className='wtfExamplesContainer'>
        <Examples onExampleClicked={this.onExampleClicked} />
      </div>
    );
  }
}

/**
 * Set the ExamplesContainer component props default values.
 * @type {Object}
 */
ExamplesContainer.defaultProps = {
  uploadImageUrl: () => {},
  setError: () => {},
  setCropEndpointCalled: () => {},
  setCropData: () => {},
  redirectToCrop: () => {},
  setImageDimensions: () => {},
  setDrawingMode: () => {},
};

/**
 * Declare the ExamplesContainer component PropTypes for better validation.
 * @type {Object}
 * @property {Function} uploadImageUrl        This callback will upload the image url when it is ready.
 * @property {Function} setError              This callback will set an error.
 * @property {Function} setCropEndpointCalled Function that sets the flag telling if the crop endpoint has
 *                                            been called. By setting this to false, the text ID process can
 *                                            begin with the new image.
 * @property {Function} setCropData           This callback will update the crop data in empty when a new image
 *                                            is loaded.
 * @property {Function} redirectToCrop        This callback will redirect to the crop component when necessary.
 * @property {Function} setImageDimensions    Once the upload process is completed, this will be called to save the
 *                                            uploaded image metadata in the store.
 * @property {Function} setDrawingMode        Callback that sets the drawing mode flag in the store.
 */
ExamplesContainer.propTypes = {
  uploadImageUrl: PropTypes.func,
  setError: PropTypes.func,
  setCropEndpointCalled: PropTypes.func,
  setCropData: PropTypes.func,
  redirectToCrop: PropTypes.func,
  setImageDimensions: PropTypes.func,
  setDrawingMode: PropTypes.func,
};

/**
 * It dispatches store action and returns an object of actions that will be merged with component props.
 * @param   {Object} dispatch bind action creators.
 * @returns {Object}
 */
const mapDispatchToProps = dispatch => ({
  redirectToCrop: () => dispatch(push(appRoutes.crop)),
  uploadImageUrl: imageUrl => dispatch(uploadImageUrl(imageUrl)),
  setError: error => dispatch(setError(error)),
  setCropEndpointCalled: flag => dispatch(setCropEndpointCalled(flag)),
  setCropData: cropData => dispatch(setCropData(cropData)),
  setImageDimensions: metadata => dispatch(setImageDimensions(metadata)),
  setDrawingMode: drawingMode => dispatch(setDrawingMode(drawingMode)),
});

/**
 * Connect component with the store and actions.
 * @type {Object}
 */
export default connect(
  null,
  mapDispatchToProps
)(ExamplesContainer);



// WEBPACK FOOTER //
// ./src/components/Upload/ExamplesContainer/ExamplesContainer.js